<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted, PropType, watch } from "vue";
import { SlimOrder } from "@/types/order/Order";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import OrderCardSimple from "@/components/organisms/orders/OrderCardSimple.vue";
import { useCustomRouter } from "@/utils/router";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import { orderStore } from "@/store/order";
import { useToast } from "@/utils/toast";
import {
  AnalyzeWatchLaterEvent,
  eventService
} from "@/app/services/EventService";
import { formatFromISOString } from "@/utils/date";
import { authStore } from "@/store/auth";
import { officeService } from "@/app/services/OfficeService";

const props = defineProps({
  orders: {
    type: Array as PropType<SlimOrder[]>,
    required: true
  },
  isAllOrdersLoaded: {
    type: Boolean,
    required: true
  },
  pageTitleWhenEmpty: {
    type: String,
    required: true
  },
  transitionSource: {
    type: String as PropType<
      "SearchLimitedOrders" | "SearchIndividualOfferOrders"
    >,
    required: true
  }
});

const emits = defineEmits(["fetch-orders"]);

const loader = useLoader();
const { showErrorModal } = useErrorModal();
const { pushIgnoreError } = useCustomRouter();
const toast = useToast();
const instance = getCurrentInstance();

const evaluations = computed(() => orderStore.evaluations || {});
const watchLaterOrders = computed(() => orderStore.watchLaterOrderList || []);
const isLogin = computed(() => authStore.existToken);

const fetchEvaluation = async (officeIds: number[]) => {
  const existsOrderIds = Object.keys(evaluations.value);
  const requestOfficeIds = [
    ...new Set(
      officeIds.filter(
        officeId => !existsOrderIds.includes(officeId.toString())
      )
    )
  ];
  if (requestOfficeIds.length === 0) {
    return;
  }
  try {
    const evaluationsResponse = await officeService.fetchEvaluations({
      officeIds: requestOfficeIds
    });
    orderStore.addEvaluations(evaluationsResponse);
  } catch (e) {
    showErrorModal(e as Error);
  }
};

watch(
  () => props.orders,
  async () => {
    await fetchEvaluation(props.orders.map(order => order.office.id));
  }
);

const ordersAggregateByDate = computed(() => {
  const ordersByDate: {
    date: string;
    orders: SlimOrder[];
  }[] = [];
  props.orders.forEach(order => {
    if (ordersByDate.find(o => o.date === order.date)) {
      // 既に表示中の日付と同日の案件があれば、
      // その日付の案件リストに追加する
      ordersByDate.find(o => o.date === order.date)?.orders.push(order);
    } else {
      // 表示中の案件の日付と異なる場合は、新たに日付を追加する
      ordersByDate.push({
        date: order.date,
        orders: [order]
      });
    }
  });
  return ordersByDate;
});

onMounted(async () => {
  loader.show();
  try {
    await fetchEvaluation(props.orders.map(order => order.office.id));

    // 画面下部までスクロールされたときに追加の案件を読み込む
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 画面下部までスクロールされる && 未読み込みの案件がある場合に追加の案件を読み込む
        if (entry.isIntersecting && !props.isAllOrdersLoaded) {
          emits("fetch-orders");
        }
      },
      { threshold: 0 }
    );
    const bottomRef = instance?.proxy!.$refs.bottomRef as HTMLElement;
    observer.observe(bottomRef);
  } catch (e) {
    showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
});

const getEvaluation = (order: SlimOrder) => {
  if (!authStore.existToken) {
    return null;
  }
  return evaluations.value[order.office.id] || null;
};

const isRegisteredWatchLater = (order: SlimOrder) => {
  return !!watchLaterOrders.value.find(o => o.id === order.id);
};

const onWatchLaterChange = (order: SlimOrder) => {
  const isRegistered = isRegisteredWatchLater(order);
  if (isRegistered) {
    orderStore.removeFromWatchLaterOrderList(order.id);
  } else {
    orderStore.addToWatchLaterOrderList(order);
  }
  toast.toast(
    isRegistered
      ? "あとで見るリストから削除しました"
      : "あとで見るリストに登録しました",
    {
      toastClass: "toast-custom",
      toaster: "router-link-toaster",
      autoHideDelay: 2000,
      to: "/mypage/watch-later-orders"
    }
  );
  // イベントログを記録する。非同期で十分なのでawaitしない
  eventService.registerEvent(
    AnalyzeWatchLaterEvent.group,
    isRegistered
      ? AnalyzeWatchLaterEvent.types.removeOnSearchPage
      : AnalyzeWatchLaterEvent.types.addOnSearchPage,
    undefined,
    JSON.stringify({
      watchLaterCount: orderStore.watchLaterOrderList.length,
      orderId: order.id
    })
  );
};

const formatDate = (date: string) => {
  return formatFromISOString(date, "yyyy年M月d日(iiiii)");
};

const onCardClick = async (orderId: number) => {
  await pushIgnoreError({
    name: "OrdersDetail",
    params: { orderId: `${orderId}` },
    query: {
      transitionSource: props.transitionSource
    }
  });
};
</script>

<template>
  <div class="order-tab">
    <!-- ノッチ対応用DOM -->
    <div class="search-header-notch"></div>
    <slot name="orderTab" />
  </div>
  <div className="orders-area">
    <slot name="ad" />
    <div v-if="ordersAggregateByDate.length > 0">
      <div v-for="{ date, orders } in ordersAggregateByDate" :key="date">
        <div
          class="d-flex justify-content-center my-2"
          data-testid="order-date"
        >
          {{ formatDate(date) }}
        </div>
        <OrderCardSimple
          v-for="order in orders"
          :key="order.id"
          :order="order"
          :isLogin="isLogin"
          :isRegisteredWatchLater="isRegisteredWatchLater(order)"
          :id="order.id"
          class="my-2 mx-2"
          :evaluation="getEvaluation(order)"
          @card-click="onCardClick"
          @change-watch-later="onWatchLaterChange(order)"
          data-testid="order-card"
        />
      </div>
    </div>
    <div v-else class="empty-orders" data-testid="empty-orders">
      <p class="font-weight-bold text-mild mb-4">
        {{ pageTitleWhenEmpty }}
      </p>
      <img
        src="@/assets/svg/firstBootSurvey/caikuma_03.svg"
        class="caikuma-jump"
      />
      <p class="empty-orders-text" data-testid="empty-orders-text-limited">
        <slot name="emptyDescription" />
      </p>
    </div>
  </div>
  <p
    v-if="isAllOrdersLoaded"
    class="p-3 bg-white text-14 text-center"
    data-testid="is-all-orders-loaded"
  >
    ここが最後です
  </p>
  <div ref="bottomRef" :hidden="ordersAggregateByDate.length === 0" />
  <AppFooter />
</template>

<style lang="scss" scoped>
.search-header-notch {
  height: env(safe-area-inset-top, 0);
}

.order-tab {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100vw;
  background-color: $white;
}

.orders-area {
  margin-top: calc(env(safe-area-inset-top, 0) + 50px);
}

.empty-orders {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 80px;

  &-text {
    margin-top: 16px;
    text-align: center;
  }
}
</style>
